/**
 * Logger Utility for Mobile App
 * 🔒 SECURITY: Centralized logging with environment-based filtering
 * Production logs are sent to Sentry for monitoring
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

// Type-safe log data - can be primitives, objects, arrays, or Error
type LogData = 
  | string 
  | number 
  | boolean 
  | null 
  | undefined
  | Error
  | Record<string, unknown>
  | unknown[]
  | Date;

interface LogEntry {
  level: LogLevel;
  message: string;
  data?: LogData;
  timestamp: number;
}

class Logger {
  private isDevelopment: boolean;
  private logHistory: LogEntry[] = [];
  private maxHistorySize: number = 100;
  private sentryInitialized: boolean = false;

  constructor() {
    // Check if we're in development mode
    this.isDevelopment = __DEV__ || process.env.NODE_ENV !== 'production';
    this.initializeSentry();
  }

  /**
   * Initialize Sentry for production error tracking
   * 🔒 SECURITY: Errors are automatically sent to Sentry in production
   */
  private initializeSentry(): void {
    if (this.isDevelopment) {
      return; // Skip Sentry in development
    }

    try {
      // Dynamic import to avoid errors if Sentry is not installed
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const Sentry = require('@sentry/react-native');

      if (Sentry && typeof Sentry.captureException === 'function') {
        this.sentryInitialized = true;
      }
    } catch (e) {
      // Sentry not installed or initialization failed
      console.warn('Sentry not available:', e);
    }
  }

  /**
   * Send error to Sentry if initialized
   * @param message Error message
   * @param data Additional context data
   */
  private sendToSentry(message: string, data?: LogData): void {
    if (!this.sentryInitialized) return;

    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const Sentry = require('@sentry/react-native');

      // Create Error object for better stack traces
      const error = new Error(message);

      // Sanitize data before sending (remove sensitive info)
      const sanitizedData = this.sanitizeData(data);

      Sentry.captureException(error, {
        extra: sanitizedData,
        level: 'error',
      });
    } catch (e) {
      // Fail silently - don't break app if Sentry fails
      console.error('Failed to send to Sentry:', e);
    }
  }

  /**
   * Sanitize data to remove sensitive information
   * 🔒 SECURITY: Remove tokens, passwords, PII before logging
   */
  private sanitizeData(data: unknown): Record<string, unknown> | unknown[] | unknown {
    if (!data || typeof data !== 'object') {
      return data;
    }

    const sensitiveKeys = [
      'password',
      'token',
      'authToken',
      'accessToken',
      'refreshToken',
      'tempToken',
      'jwt',
      'apiKey',
      'secret',
      'authorization',
      'creditCard',
      'ssn',
      'cvv',
      'pin',
    ];

    // Handle arrays
    if (Array.isArray(data)) {
      return data.map(item => this.sanitizeData(item));
    }

    // Handle objects
    const sanitized: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(data)) {
      const lowerKey = key.toLowerCase();
      const isSensitive = sensitiveKeys.some(sensitiveKey =>
        lowerKey.includes(sensitiveKey.toLowerCase())
      );

      if (isSensitive) {
        sanitized[key] = '[REDACTED]';
      } else if (typeof value === 'object' && value !== null) {
        sanitized[key] = this.sanitizeData(value);
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }

  private log(level: LogLevel, message: string, data?: LogData): void {
    const entry: LogEntry = {
      level,
      message,
      data,
      timestamp: Date.now(),
    };

    // Store in history
    this.logHistory.push(entry);
    if (this.logHistory.length > this.maxHistorySize) {
      this.logHistory.shift();
    }

    // In production, only log errors and warnings
    if (!this.isDevelopment && level !== 'error' && level !== 'warn') {
      return;
    }

    // Format log message
    const prefix = `[${level.toUpperCase()}]`;
    const timestamp = new Date(entry.timestamp).toISOString();
    const logMessage = `${prefix} [${timestamp}] ${message}`;

    // Use native console methods
    switch (level) {
      case 'debug':
        if (this.isDevelopment) {
          console.log(logMessage, data || '');
        }
        break;
      case 'info':
        if (this.isDevelopment) {
          console.info(logMessage, data || '');
        }
        break;
      case 'warn':
        console.warn(logMessage, data || '');
        break;
      case 'error':
        console.error(logMessage, data || '');
        break;
    }

    // 🔒 SECURITY: Send errors to Sentry in production
    if (!this.isDevelopment && level === 'error') {
      this.sendToSentry(message, data);
    }
  }

  debug(message: string, data?: LogData): void {
    this.log('debug', message, data);
  }

  info(message: string, data?: LogData): void {
    this.log('info', message, data);
  }

  warn(message: string, data?: LogData): void {
    this.log('warn', message, data);
  }

  error(message: string, data?: LogData): void {
    this.log('error', message, data);
  }

  /**
   * Get log history (useful for debugging)
   */
  getHistory(): LogEntry[] {
    return [...this.logHistory];
  }

  /**
   * Clear log history
   */
  clearHistory(): void {
    this.logHistory = [];
  }

  /**
   * Get logs filtered by level
   */
  getLogsByLevel(level: LogLevel): LogEntry[] {
    return this.logHistory.filter(entry => entry.level === level);
  }
}

// Export singleton instance
export const logger = new Logger();

// Export default for convenience
export default logger;

